import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import HeaderCancel from '@/components/dashboard/Header/HeaderCancel'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import MotionList from '@/components/common/Motion/MotionList'
import LanguageSelector from '@/components/common/LanguageSelector/LanguageSelector'

import {
  PiCaretRight,
  PiLink,
  PiBook,
  PiCar,
  PiCookie,
  PiGavel,
  PiShield,
  PiUser,
  PiScales,
  PiLeaf,
  PiYoutubeLogo,
  PiReceipt,
  PiChat,
  PiGlobe
} from 'react-icons/pi'

import { Crisp } from 'crisp-sdk-web'

import GoogleDriveIcon from '@/components/pages/Expenses/Drive/GoogleDriveIcon'
import DriveSheets from '@/components/pages/Expenses/Drive/DriveSheets'

import { useUserStore } from '@/store/useUserStore'
import { useDriveStore } from '@/store/useDriveStore'
import { usePageSetup } from '@/hooks/usePageSetup'
import { SUPPORTED_LANGUAGES, Language } from '@/i18n'

import { VAT_LINKS } from '@/components/pages/Dashboard/DashboardVatMemo'

export const VMI_TAXES_PDF = "https://www.vmi.lt/evmi/documents/20142/730461/Atmintin%C4%97+-+Mokes%C4%8Diai%2C+kurie+yra+mokami+%C4%AFregistravus+individuali%C4%85+maisto+kurjerio+ar+keleivi%C5%B3+pav%C4%97%C5%BE%C4%97jo+veikl%C4%85.pdf/79357bd2-40e7-e628-8144-0e8adb99a17f";
export const VAT_HOW_PDF = "https://www.vmi.lt/evmi/documents/20142/730461/PVM+PRIEVOLES+APMOKESTINAMIESIEMS+ASMENIMS+ISIGYJANTIEMS+PASLAUGAS+IS+UZSIENIO+VALSTYBIU%2C+NUO+2025+M.+GEGUZES+1+DIENOS.pdf/281346b8-dd49-5d52-609a-60e1f1592230?t=1748232695109"
export const VMI_ALLOWED_DEDUCTIONS_LINK = "https://www.vmi.lt/evmi/leid%C5%BEiami-/-neleid%C5%BEiami-atskaitymai1#kurios-i%C5%A1laidos-laikomos-gyventojo-kuris-ver%C4%8Diasi-individualia-veikla-leid%C5%BEiamais-atskaitymais"

export const ProfilePage = () => {
  const { t, i18n } = useTranslation()

  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)

  const driveStatus = useUserStore((state) => state.user?.google_drive_connection?.status ?? 'disconnected')
  const openDriveSheet = useDriveStore((state) => state.openSheet)

  const [isLanguageOpen, setIsLanguageOpen] = useState(false)

  usePageSetup(t('profile.title'), { scroll: true })

  const hourGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 5 && hour < 12) {
      return t('profile.greetings.morning');
    } else if (hour >= 12 && hour < 17) {
      return t('profile.greetings.afternoon');
    } else if (hour >= 17 && hour < 22) {
      return t('profile.greetings.evening');
    } else {
      return t('profile.greetings.night');
    }
  }

  const openChat = () => {
    if (user) {
      Crisp.user.setEmail(user.email)
      Crisp.user.setNickname(
        `${user.kyc?.first_name} ${user.kyc?.last_name}`
      )
    }
    Crisp.chat.open()
  }

  const items = [
    {
      id: "language",
      title: t('profile.language'),
      subtitle: SUPPORTED_LANGUAGES[i18n.language as Language].nativeName,
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiGlobe className="h-6 w-6 text-zinc-900" />,
      groupId: "priority",
      props: {
        onClick: () => setIsLanguageOpen(true)
      }
    },
    {
      id: "chat",
      title: t('profile.liveChat'),
      subtitle: t('profile.liveChatDescription'),
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiChat className="h-6 w-6 text-zinc-900" />,
      groupId: "priority",
      props: {
        onClick: openChat
      }
    },
    {
      id: "edit",
      title: [user?.kyc.first_name, user?.kyc.last_name].join(" "),
      subtitle: user?.email,
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiUser className="h-6 w-6 text-zinc-900" />,
      groupId: "account",
      props: {
        onClick: () => navigate(`/profile/account`)
      }
    },
    {
      id: "personal",
      title: user?.kyc.address || "",
      subtitle: user?.kyc.business_certificate_number || "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiBook className="h-6 w-6 text-zinc-900" />,
      groupId: "account",
      props: {
        onClick: () => navigate(`/kyc/details`, { state: { profile: true } })
      }
    },
    {
      id: "insuranceStatus",
      title: t('profile.insuranceStatus'),
      subtitle: user?.kyc?.insurance_status
        ? t(`kyc.insuranceStatus.options.${user.kyc.insurance_status}`)
        : t('profile.insuranceStatusNotSet'),
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiReceipt className="h-6 w-6 text-zinc-900" />,
      groupId: "account",
      props: {
        onClick: () => navigate(`/kyc/insurance-status`, { state: { returnTo: '/profile' } })
      }
    },
    {
      id: "depreciation",
      title: t('profile.depreciation'),
      subtitle: t('profile.depreciationSoon'),
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiCar className="h-6 w-6 text-zinc-900" />,
      groupId: "account",
      props: {
        className: "opacity-20"
      }
    },
    {
      id: "pollution",
      title: t('profile.pollution'),
      subtitle: t('profile.depreciationSoon'),
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiLeaf className="h-6 w-6 text-zinc-900" />,
      groupId: "account",
      props: {
        className: "opacity-20"
      }
    },
    {
      id: "handbook",
      title: t('profile.driverHandbook'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiBook className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => navigate(`/handbook/journal`)
      }
    },
    {
      id: "expenses-memo",
      title: t('profile.expensesMemo'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiReceipt className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => navigate(`/expenses/memo`)
      }
    },
    {
      id: "vat-guide",
      title: t('profile.whatIsReverseVat'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => navigate(`/vat/memo`)
      }
    },
    {
      id: "what-taxes",
      title: t('profile.taxesForDrivers'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open(VMI_TAXES_PDF, '_blank')
      }
    },
    {
      id: "vat",
      title: t('profile.pvmSvsRegistration'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open(VAT_HOW_PDF, '_blank')
      }
    },
    {
      id: "vmi-payments",
      title: t('profile.paymentOfContributions'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open(VAT_LINKS.vmiPayment, '_blank')
      }
    },
    {
      id: "allowed-deductions",
      title: t('profile.allowedDeductions'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open(VMI_ALLOWED_DEDUCTIONS_LINK, '_blank')
      }
    },
    {
      id: "sodra-iv",
      title: t('profile.individualActivityContributions'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open('https://sodra.lt/imokos/vykdau-individualia-veikla', '_blank')
      }
    },
    {
      id: "sodra-tarifai",
      title: t('profile.vsdPsdRates'),
      subtitle: "",
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiScales className="h-6 w-6 text-zinc-900" />,
      groupId: "knowledgebase",
      props: {
        onClick: () => window.open(VAT_LINKS.sodraTarifai, '_blank')
      }
    },
    {
      id: 'mps-video',
      title: t('profile.mpsInstallments'),
      subtitle: '',
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiYoutubeLogo className="h-6 w-6 text-zinc-900" />,
      groupId: "video",
      props: {
        onClick: () => window.open('https://www.youtube.com/watch?v=E-43-Tm_5BM', '_blank')
      }
    },
    {
      id: 'ilgalaikis-turtas',
      title: t('profile.longTermAssets'),
      subtitle: '',
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiYoutubeLogo className="h-6 w-6 text-zinc-900" />,
      groupId: "video",
      props: {
        onClick: () => window.open('https://www.youtube.com/watch?v=Vw0ZktZlOW0', '_blank')
      }
    },
    {
      id: 'vatvideo-2025-05-01',
      title: t('profile.vatObligationFrom2025'),
      subtitle: '',
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiYoutubeLogo className="h-6 w-6 text-zinc-900" />,
      groupId: "video",
      props: {
        onClick: () => window.open('https://www.youtube.com/watch?v=LmBFZMByiX4', '_blank')
      }
    },
    {
      id: 'vatvideo',
      title: t('profile.smallBusinessScheme'),
      subtitle: '',
      value: <PiLink className="h-4 w-4 text-zinc-900" />,
      icon: <PiYoutubeLogo className="h-6 w-6 text-zinc-900" />,
      groupId: "video",
      props: {
        onClick: () => window.open("https://www.youtube.com/watch?v=cfScPLOkKZk", '_blank')
      }
    },
    {
      id: "google-drive",
      title: "Google Drive",
      subtitle: t(`profile.driveStatus.${driveStatus}`),
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <GoogleDriveIcon className="h-6 w-6" />,
      groupId: "integrations",
      props: {
        onClick: openDriveSheet
      }
    },
    {
      id: "terms-of-service",
      title: t('common.termsOfService'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiGavel className="h-6 w-6 text-zinc-900" />,
      groupId: "more",
      props: {
        onClick: () => navigate('/legal/terms-of-service')
      }
    },
    {
      id: "privacy-policy",
      title: t('common.privacyPolicy'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiShield className="h-6 w-6 text-zinc-900" />,
      groupId: "more",
      props: {
        onClick: () => navigate('/legal/privacy-policy')
      }
    },
    {
      id: "cookie-policy",
      title: t('common.cookiePolicy'),
      subtitle: "",
      value: <PiCaretRight className="h-4 w-4 text-zinc-900" />,
      icon: <PiCookie className="h-6 w-6 text-zinc-900" />,
      groupId: "more",
      props: {
        onClick: () => navigate('/legal/cookie-policy')
      }
    }
  ]

  const groups = [
    {
      id: "priority",
      name: "",
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    },
    {
      id: "account",
      name: t('profile.account'),
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    },
    {
      id: "integrations",
      name: t('profile.integrations'),
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    },
    {
      id: "knowledgebase",
      name: t('profile.handbook'),
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    },
    {
      id: "video",
      name: t('profile.videos'),
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    },
    {
      id: "more",
      name: t('profile.general'),
      description: "",
      props: {
        className: "border-b border-zinc-200 pb-4"
      }
    }
  ]

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderCancel to={"/"} />
      <div className="flex-1 p-5 pt-2 pb-[4rem]">
        <MotionDiv className='mb-5'>
          <h1 className='text-base-black text-3xl font-semibold'>
            {hourGreeting()}, {user?.kyc?.first_name}
          </h1>
        </MotionDiv>
        <MotionList key={i18n.language} items={items} groups={groups} />
      </div>
      <DriveSheets />
      <LanguageSelector
        hideButton
        isOpen={isLanguageOpen}
        setOpen={setIsLanguageOpen}
      />
    </div>
  )
}

export default ProfilePage